import { createSelector } from "@reduxjs/toolkit";
import {
  OPPORTUNITY_SOURCES,
  OPPORTUNITY_TYPES,
} from "constants/opportunities";
import { isEmpty, isNil } from "lodash";
import { getHumanizedStatus } from "utils/opportunities";

import { selectOpportunityState } from "../selectors";

const isNullish = (value) => isNil(value) || isEmpty(value);

const hasFieldMatch = (fieldValue, searchBy) => {
  if (isNullish(fieldValue)) return false;

  const regexp = new RegExp(searchBy, "gi");

  return Boolean(fieldValue.match(regexp));
};

export const selectTrackedState = createSelector(
  [selectOpportunityState],
  (opportunity) => opportunity?.tracked
);

export const selectTrackedData = createSelector(
  [selectTrackedState],
  (tracked) => tracked?.data || []
);

export const selectSearchBy = createSelector(
  [selectTrackedState],
  (tracked) => tracked?.searchBy
);

export const selectIsBestFit = (state) =>
  Boolean(state?.opportunity?.tracked?.isBestFit);

export const selectFilteredTrackedData = createSelector(
  [selectTrackedData, selectSearchBy, selectIsBestFit],
  (data, searchBy, isBestFit) => {
    if (isBestFit) return data;

    if (!searchBy || !data.length) return data;

    return data.filter((opp) => {
      const {
        classificationCode,
        naicsCode,
        opportunitySource,
        setAside,
        title,
        type,
      } = opp;
      const agency = opp.customer || opp.departmentAgency;
      const status = getHumanizedStatus(opp);
      const oppSource = OPPORTUNITY_SOURCES[opportunitySource];
      const oppType = OPPORTUNITY_TYPES[opp?.opportunityType];

      return (
        hasFieldMatch(title, searchBy) ||
        hasFieldMatch(agency, searchBy) ||
        hasFieldMatch(oppSource, searchBy) ||
        hasFieldMatch(oppType, searchBy) ||
        hasFieldMatch(classificationCode, searchBy) ||
        hasFieldMatch(naicsCode, searchBy) ||
        hasFieldMatch(status, searchBy) ||
        hasFieldMatch(type, searchBy) ||
        hasFieldMatch(setAside, searchBy)
      );
    });
  }
);

export const selectIsLoading = (state) =>
  Boolean(state?.opportunity?.tracked?.isLoading);

export const selectIsInitialized = (state) =>
  Boolean(state?.opportunity?.tracked?.isInitialized);

export const selectIsCountInitialized = (state) =>
  Boolean(state?.opportunity?.tracked?.isCountInitialized);

export const selectSelectedRowIndex = (state) =>
  state?.opportunity?.tracked?.selectedRowIndex || -1;

export const selectBestFitSortBy = (state) =>
  state?.opportunity?.tracked?.bestFitSortBy;

export const selectCurrentCount = (state) =>
  state?.opportunity?.tracked?.currentCount;

export const selectTotalCount = (state) =>
  state?.opportunity?.tracked?.totalCount;

export const selectSorting = (state) => state?.opportunity?.tracked?.sorting;

export const selectLastUpdatedValue = (state) =>
  state?.opportunity?.tracked?.lastUpdatedValue;

export const selectIsCustomLastUpdated = (state) =>
  state?.opportunity?.tracked?.isCustomLastUpdated;

export const selectAdditionalFilters = (state) =>
  state?.opportunity?.tracked?.additionalFilters;

export const selectOpportunityKanbanColumns = createSelector(
  [selectTrackedState],
  (tracked) => tracked?.kanbanColumns || []
);

export const selectIsOpportunityKanbanStale = (state) =>
  Boolean(state?.opportunity?.tracked?.isKanbanStale);

export const selectOpportunityGateMode = (state) =>
  state?.opportunity?.tracked?.gateMode;
